import { ArrowRight, Loader2, Shield, UserRound } from "lucide-react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  getTurnstileSiteKey,
  getTurnstileWidgetErrorUserMessage,
  loadTurnstileScript,
  verifyTurnstileToken,
} from "@/utils/turnstile";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          size?: string;
          theme?: "light" | "dark" | "auto";
          callback?: (token: string) => void;
          "error-callback"?: (errorCode?: string) => void;
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
    };
  }
}

const patientRegistrationSchema = z.object({
  name: z.string().trim().min(1, "Nome completo é obrigatório"),
  email: z.string().email("Por favor, insira um e-mail válido"),
  password: z.string().min(8, "A senha deve ter pelo menos 8 caracteres"),
});

export type PatientRegistrationForm = z.infer<typeof patientRegistrationSchema>;

interface Step1PatientRegistrationProps {
  formData: PatientRegistrationForm;
  onFormChange: (data: Partial<PatientRegistrationForm>) => void;
  onRegister: (data: PatientRegistrationForm) => Promise<void>;
  isRegistering: boolean;
  /** Quando vindo da landing com query `focus=name|email|password` */
  initialFocus?: "name" | "email" | "password";
}

export function Step1PatientRegistration({
  formData,
  onFormChange,
  onRegister,
  isRegistering,
  initialFocus,
}: Step1PatientRegistrationProps) {
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileTokenRef = useRef<string | null>(null);

  const form = useForm<PatientRegistrationForm>({
    resolver: zodResolver(patientRegistrationSchema),
    defaultValues: formData,
    values: formData,
  });

  useEffect(() => {
    if (!initialFocus) return;
    const id =
      initialFocus === "name"
        ? "name"
        : initialFocus === "email"
          ? "email"
          : "password";
    requestAnimationFrame(() => {
      document.getElementById(id)?.focus();
    });
  }, [initialFocus]);

  useLayoutEffect(() => {
    const siteKey = getTurnstileSiteKey();
    if (!siteKey) return;

    const renderWidget = () => {
      if (!turnstileRef.current || !window.turnstile) return;

      const widgetId = window.turnstile.render(turnstileRef.current, {
        sitekey: siteKey,
        size: "normal",
        theme: "light",
        callback: (token: string) => {
          turnstileTokenRef.current = token;
        },
        "error-callback": (errorCode?: string) => {
          turnstileTokenRef.current = null;
          toast.error(getTurnstileWidgetErrorUserMessage(errorCode));
        },
      });
      turnstileWidgetId.current = widgetId;
    };

    const scheduleRender = () => {
      renderWidget();
      if (turnstileWidgetId.current) return;
      requestAnimationFrame(() => {
        renderWidget();
        if (!turnstileWidgetId.current) {
          setTimeout(renderWidget, 500);
        }
      });
    };

    void loadTurnstileScript().then(scheduleRender);

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, []);

  const handleSubmit = form.handleSubmit(async (data) => {
    const siteKey = getTurnstileSiteKey();
    if (siteKey) {
      if (!turnstileWidgetId.current || !window.turnstile) {
        toast.error(
          "Verificação de segurança não carregada. Recarregue a página.",
        );
        return;
      }

      let turnstileToken = turnstileTokenRef.current;
      if (!turnstileToken) {
        turnstileToken = window.turnstile.getResponse(
          turnstileWidgetId.current,
        );
      }

      if (!turnstileToken) {
        toast.error("Por favor, complete a verificação de segurança");
        window.turnstile.reset(turnstileWidgetId.current);
        turnstileTokenRef.current = null;
        return;
      }

      const verificationResult = await verifyTurnstileToken(turnstileToken);
      if (!verificationResult.success) {
        toast.error(
          verificationResult.error ||
            "Verificação de segurança falhou. Tente novamente.",
        );
        window.turnstile.reset(turnstileWidgetId.current);
        turnstileTokenRef.current = null;
        return;
      }
    }

    await onRegister(data);
  });

  return (
    <section className="bg-white border-2 rounded-2xl p-8 shadow-2xl space-y-6 animate-fade-in max-w-2xl mx-auto">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center mb-4">
          <UserRound className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold">Cadastro do paciente</h2>
        <p className="text-muted-foreground">
          Crie sua conta para acessar sua área de saúde pessoal.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">
            Nome completo <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name"
            placeholder="Seu nome completo"
            {...form.register("name", {
              onChange: (e) => onFormChange({ name: e.target.value }),
            })}
          />
          {form.formState.errors.name && (
            <p className="text-sm text-destructive">
              {form.formState.errors.name.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">
            E-mail <span className="text-destructive">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="seu@email.com"
            {...form.register("email", {
              onChange: (e) => onFormChange({ email: e.target.value }),
            })}
          />
          {form.formState.errors.email && (
            <p className="text-sm text-destructive">
              {form.formState.errors.email.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">
            Senha <span className="text-destructive">*</span>
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            {...form.register("password", {
              onChange: (e) => onFormChange({ password: e.target.value }),
            })}
          />
          {form.formState.errors.password && (
            <p className="text-sm text-destructive">
              {form.formState.errors.password.message}
            </p>
          )}
        </div>

        {getTurnstileSiteKey() ? (
          <div className="flex flex-col items-center justify-center py-2 min-h-[65px]">
            <div
              ref={turnstileRef}
              id="turnstile-widget-register-patient"
              className="w-full flex justify-center"
              style={{ minHeight: "65px" }}
            />
          </div>
        ) : null}

        <div className="flex items-center justify-end pt-4">
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={isRegistering}
          >
            {isRegistering ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Criando conta...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Criar conta
                <ArrowRight className="h-5 w-5" />
              </span>
            )}
          </Button>
        </div>
      </form>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
        <Shield className="h-4 w-4" />
        <span>Seus dados são protegidos conforme a LGPD</span>
      </div>
    </section>
  );
}
